/**
 * Data Sanitization Utilities
 *
 * These functions sanitize data before submitting to the API.
 * Unlike normalizers (which transform), sanitizers validate and reject invalid data.
 * Returns undefined for invalid values, which the API interprets as optional.
 *
 * Ported from mobile/utils/team-transformer.ts for full feature parity.
 */

import { VALIDATION_RANGES, FIELD_LIMITS } from '../constants/validation';

/**
 * Sanitize null-like string values to undefined
 * Converts "NULL", "null", "N/A", "NA", "none", "-", empty strings to undefined
 * @param value - Raw value to sanitize
 * @returns Sanitized value or undefined for null-like values
 */
export function sanitizeValue(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value !== 'string') {
    const str = String(value).trim();
    return str || undefined;
  }

  const trimmed = value.trim();
  const nullLikeValues = ['null', 'n/a', 'na', 'none', '-', ''];

  if (nullLikeValues.includes(trimmed.toLowerCase())) {
    return undefined;
  }

  return trimmed || undefined;
}

/**
 * A phone number out of whatever the roster's phone cell holds.
 *
 * Everything that is not a digit is dropped, so "Cell: 859-609-3174", "(859) 609-3174" and
 * "859.609.3174" all reach the API as the same number, and a cell with no digits at all ("N/A")
 * sends nothing rather than junk. Ten digits, or eleven behind a country code of 1, are written
 * ###-###-#### to match how numbers are already stored; anything else keeps its digits.
 *
 * Mirrors RosterPhone.Normalize on the API, which is what actually guards the column. This exists
 * so the import preview shows what will be stored, and so a printed cell longer than the column
 * cannot be rejected by an API deployed before that helper existed.
 */
export function sanitizePhone(value: unknown): string | undefined {
  const raw = sanitizeValue(value);
  if (raw === undefined) return undefined;

  let digits = raw.replace(/\D/g, '');
  if (digits.length === 0) return undefined;
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return digits.slice(0, 50);
}

/**
 * Parse integer or return undefined
 * Safe integer parsing with validation
 * @param value - Value to parse as integer
 * @returns Parsed integer or undefined if invalid
 */
export function parseIntOrUndefined(value: unknown): number | undefined {
  const normalized = typeof value === 'string' ? sanitizeValue(value) : value;
  if (normalized == null) return undefined;
  const num = parseInt(String(normalized).replace(/^#/, ''));
  return isNaN(num) ? undefined : num;
}

/**
 * Sanitize email field - ensure it's a valid string or undefined
 * Filters out numbers that may have been incorrectly mapped from gradYear
 * @param value - Email value to sanitize
 * @returns Valid email string or undefined
 */
export function sanitizeEmail(value: unknown): string | undefined {
  // If it's a number or looks like a year, return undefined
  if (typeof value === 'number') {
    return undefined;
  }

  const normalized = sanitizeValue(value);
  if (!normalized) return undefined;

  // Filter out values that look like years (4 digits)
  if (/^\d{4}$/.test(normalized)) {
    return undefined;
  }

  // Basic email format check (contains @ and .)
  if (!normalized.includes('@') || !normalized.includes('.')) {
    return undefined;
  }

  return normalized;
}

/**
 * Sanitize handedness field (bats/throws) - must be single character L, R, or S
 * Filters out position values that may have been incorrectly mapped
 * @param value - Bats or throws value to sanitize
 * @returns "L", "R", "S", or undefined
 */
export function sanitizeHandedness(value: unknown): string | undefined {
  const normalized = sanitizeValue(value);
  if (!normalized) return undefined;

  // Must be exactly 1 character
  if (normalized.length !== 1) {
    return undefined;
  }

  // Must be L, R, or S (case insensitive)
  const upper = normalized.toUpperCase();
  if (upper !== 'L' && upper !== 'R' && upper !== 'S') {
    return undefined;
  }

  return upper;
}

/**
 * Sanitize position field - must be a valid string, not a number
 * Filters out numeric values that may have been incorrectly mapped
 * @param value - Position value to sanitize
 * @returns Valid position string or undefined
 */
export function sanitizePosition(value: unknown): string | undefined {
  // If it's a number, return undefined
  if (typeof value === 'number') {
    return undefined;
  }

  const normalized = sanitizeValue(value);
  if (!normalized) return undefined;

  // Filter out values that are purely numeric (like "55", "28", "18")
  if (/^\d+$/.test(normalized)) {
    return undefined;
  }

  // Filter out values with weird patterns like "38.55.28" or "38.55.P"
  // But allow valid multi-position notations like "OF/P" or "3B-SS"
  if (/^\d+\.\d+/.test(normalized)) {
    // Starts with number.number pattern - likely corrupted data
    return undefined;
  }

  // Max length check (position should be reasonable length)
  if (normalized.length > FIELD_LIMITS.positionMaxLength) {
    return undefined;
  }

  return normalized;
}

/**
 * Sanitize height field - validates format and reasonable range
 * @param height - Height string (e.g., "5-11", "6-2")
 * @returns Valid height string or undefined
 */
export function sanitizeHeight(height: unknown): string | undefined {
  const normalized = sanitizeValue(height);
  if (!normalized) return undefined;

  // Check format: should be "F-I" where F is feet and I is inches
  if (!/^\d+-\d+$/.test(normalized)) {
    return undefined;
  }

  const [feet, inches] = normalized.split('-').map(Number);

  // Reasonable range: 4'0" to 7'11"
  if (feet < VALIDATION_RANGES.height.minFeet || feet > VALIDATION_RANGES.height.maxFeet) {
    return undefined;
  }

  if (inches < VALIDATION_RANGES.height.minInches || inches > VALIDATION_RANGES.height.maxInches) {
    return undefined;
  }

  return normalized;
}

/**
 * Sanitize weight field - validates numeric and reasonable range
 * @param weight - Weight value (should be numeric)
 * @returns Weight string or undefined
 */
export function sanitizeWeight(weight: unknown): string | undefined {
  const normalized = sanitizeValue(weight);
  if (!normalized) return undefined;

  const num = parseFloat(normalized);
  if (isNaN(num)) return undefined;

  // Reasonable range: 100-400 lbs
  if (num < VALIDATION_RANGES.weight.min || num > VALIDATION_RANGES.weight.max) {
    return undefined;
  }

  return normalized;
}

/**
 * Sanitize graduation year - validates reasonable range
 * @param year - Graduation year (numeric)
 * @returns Year number or undefined
 */
export function sanitizeGradYear(year: unknown): number | undefined {
  if (year == null) return undefined;

  const num = typeof year === 'number' ? year : parseIntOrUndefined(year);
  if (num == undefined) return undefined;

  // Reasonable range: currentYear - 2 to currentYear + 10
  const currentYear = new Date().getFullYear();
  if (num < currentYear - 2 || num > currentYear + 10) {
    return undefined;
  }

  return num;
}
